import { z } from 'zod';
import { BlockSchema } from './blocks.js';

// ─────────────────────────────────────────────────────────────────
// Reusable components
//
// A component is a named, reusable set of blocks a user can insert into
// posts and/or emails. Inserting snapshots the blocks into the document as
// ordinary editable blocks AND records a ComponentUsage so an updated
// component can later be pushed onto all or selected documents that use it.
// ─────────────────────────────────────────────────────────────────

/** Where a component may be inserted. */
export const ComponentTargetSchema = z.enum(['post', 'email', 'both']);

export type ComponentTarget = z.infer<typeof ComponentTargetSchema>;

/**
 * One insertion of a component into a document. Persisted (JSON) on the
 * post/email row so the component library can offer "push update" with
 * per-document choice:
 *
 * - `snapshot` is the exact blocks inserted at that time — used to detect
 *   whether the author customized the instance in this document.
 * - `blockIds` locate the instance in whichever block list holds it — the
 *   root, a section, a column or an accordion item.
 * - `componentVersion` is the component version at insert time (staleness).
 */
export const ComponentUsageSchema = z.object({
  /** Unique id of this usage/instance (ulid). */
  id: z.string(),
  componentId: z.string(),
  componentVersion: z.number().int().min(0),
  /** Ids of the sibling blocks the instance occupied when inserted. */
  blockIds: z.array(z.string()),
  /** The blocks exactly as inserted (baseline for customization checks). */
  snapshot: z.array(BlockSchema),
});

export type ComponentUsage = z.infer<typeof ComponentUsageSchema>;
export type ComponentUsageInput = ComponentUsage;

export const CreateComponentSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  description: z.string().max(1000).optional().nullable(),
  target: ComponentTargetSchema.default('both'),
  blocks: z.array(BlockSchema).default([]),
});

export type CreateComponentInput = z.infer<typeof CreateComponentSchema>;

export const UpdateComponentSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(1000).optional().nullable(),
  target: ComponentTargetSchema.optional(),
  blocks: z.array(BlockSchema).optional(),
});

export type UpdateComponentInput = z.infer<typeof UpdateComponentSchema>;

export const ComponentListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  target: ComponentTargetSchema.optional(),
});

export type ComponentListQuery = z.infer<typeof ComponentListQuerySchema>;

// ─────────────────────────────────────────────────────────────────
// Push-update payloads
// ─────────────────────────────────────────────────────────────────

/** A single document that uses a component (returned by GET /components/:id/usages). */
export const ComponentUsageDocSchema = z.object({
  kind: z.enum(['post', 'email']),
  documentId: z.string(),
  title: z.string(),
  status: z.string(),
  /** Whether pushing an update is allowed (sent emails are not). */
  updatable: z.boolean(),
  instanceId: z.string(),
  /** True when this document's instance blocks differ from the inserted snapshot. */
  customized: z.boolean(),
  /** True when the component has newer content than this instance was inserted from. */
  stale: z.boolean(),
  updatedAt: z.number(),
});

export type ComponentUsageDoc = z.infer<typeof ComponentUsageDocSchema>;

export const ComponentApplyUpdateSchema = z.object({
  updates: z
    .array(
      z.object({
        kind: z.enum(['post', 'email']),
        documentId: z.string(),
        instanceId: z.string(),
        /** Overwrite the document's customized instance too (default true). */
        replace: z.boolean().default(true),
      }),
    )
    .min(1, 'At least one document must be selected'),
});

export type ComponentApplyUpdateInput = z.infer<typeof ComponentApplyUpdateSchema>;

/** Where a component may be inserted. */
export const CONTENT_TARGET_LABELS: Record<ComponentTarget, string> = {
  post: 'Posts only',
  email: 'Emails only',
  both: 'Posts & emails',
};
